import { supabase, supabaseConfig } from "./supabase.js";
import { changeInitialPassword } from "./admin-users.js";
import { validateNewPassword } from "./password.js";

const SESSION_CACHE_KEY = "pdapp_supabase_session_v1";

async function profileFor(userId) {
  const { data, error } = await supabase
    .from(supabaseConfig.profilesTable)
    .select("id,email,name,role,status,must_change_password")
    .eq("id", userId)
    .single();
  if (error?.code === "PGRST116" || !data) {
    throw Object.assign(
      new Error("Esta conta não possui um perfil de acesso válido. Fale com o superusuário."),
      { status: 403 }
    );
  }
  if (error) throw error;
  if (!data.status) {
    throw Object.assign(
      new Error("Esta conta está desativada. Fale com o superusuário."),
      { status: 403 }
    );
  }
  return data;
}

function sessionFromUser(user, profile, options = {}) {
  if (!["researcher", "admin", "superuser"].includes(profile.role)) {
    throw new Error("Esta conta não possui um perfil de acesso válido. Fale com o superusuário.");
  }
  return {
    userId: user.id,
    email: profile.email || user.email,
    username: profile.name || user.user_metadata?.name || user.email,
    role: profile.role,
    labels: [profile.role],
    mustChangePassword: Boolean(profile.must_change_password),
    signedInAt: new Date().toISOString(),
    offline: Boolean(options.offline)
  };
}

function readCachedSession() {
  try {
    const cached = JSON.parse(localStorage.getItem(SESSION_CACHE_KEY) || "null");
    return cached?.userId && cached?.role ? cached : null;
  } catch {
    return null;
  }
}

function cacheSession(session) {
  localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(session));
  return session;
}

async function currentOnlineSession() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) throw error || new Error("Sessão não encontrada.");
  return sessionFromUser(data.user, await profileFor(data.user.id));
}

export async function authenticate(emailInput, password) {
  const email = String(emailInput || "").trim().toLowerCase();
  if (!email || !password) throw new Error("Informe seu e-mail e sua senha.");

  await supabase.auth.signOut({ scope: "local" }).catch(() => {});
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password: String(password)
  });
  if (error || !data.user) {
    throw new Error(error?.message === "Invalid login credentials"
      ? "E-mail ou senha incorretos."
      : (error?.message || "Não foi possível entrar."));
  }

  try {
    return cacheSession(sessionFromUser(data.user, await profileFor(data.user.id)));
  } catch (caught) {
    await supabase.auth.signOut({ scope: "local" }).catch(() => {});
    throw caught;
  }
}

export async function restoreSession() {
  const cached = readCachedSession();
  try {
    return cacheSession(await currentOnlineSession());
  } catch (error) {
    const status = Number(error?.status || error?.code || 0);
    const authenticationFailed = status === 400 || status === 401 || status === 403;
    if (cached && !authenticationFailed) {
      return { ...cached, offline: true };
    }
    localStorage.removeItem(SESSION_CACHE_KEY);
    return null;
  }
}

export async function signOut() {
  localStorage.removeItem(SESSION_CACHE_KEY);
  const { error } = await supabase.auth.signOut({ scope: "local" });
  if (error && navigator.onLine) throw error;
}

export async function changeRequiredPassword(newPassword) {
  const password = validateNewPassword(newPassword, "A nova senha");
  const cached = readCachedSession();
  if (!cached?.email) throw new Error("A sessão local não contém o e-mail da conta.");

  await changeInitialPassword(password);
  await supabase.auth.signOut({ scope: "local" }).catch(() => {});
  const { error } = await supabase.auth.signInWithPassword({
    email: cached.email,
    password
  });
  if (error) throw new Error("A senha foi alterada, mas é necessário entrar novamente.");
  return cacheSession(await currentOnlineSession());
}
